import React, { useState, useEffect } from "react";
import { Link } from 'react-router-dom';

const Roles = () => {
  const [showSidebar, setShowSidebar] = useState(false);
  const [roleTitle, setRoleTitle] = useState("");
  const [isMobile, setIsMobile] = useState(false);
  const [errors, setErrors] = useState({
    roleTitle: "",
    permissions: ""
  });
  const [allPermissions, setAllPermissions] = useState([
    { id: "perm1", name: "Permission 1", checked: false },
    { id: "perm2", name: "Permission 2", checked: false },
    { id: "perm3", name: "Permission 3", checked: false }
  ]);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentRoleId, setCurrentRoleId] = useState(null);
  const [roles, setRoles] = useState([
    { 
      id: 1, 
      title: "Admin", 
      permissions: ["perm1", "perm2"], 
      status: "Active" 
    },
    { 
      id: 2, 
      title: "Editor", 
      permissions: ["perm2"], 
      status: "Active" 
    },
    { 
      id: 3, 
      title: "Viewer", 
      permissions: ["perm1"], 
      status: "Inactive" 
    }
  ]);

  const validateForm = () => {
    let valid = true;
    const newErrors = {
      roleTitle: "",
      permissions: ""
    };

    // Role Title validation
    if (!roleTitle.trim()) {
      newErrors.roleTitle = "Role title is required.";
      valid = false;
    } else if (roleTitle.length < 3) {
      newErrors.roleTitle = "Role title must be at least 3 characters.";
      valid = false;
    }

    // Permissions validation
    const hasSelectedPermissions = allPermissions.some(perm => perm.checked);
    if (!hasSelectedPermissions) {
      newErrors.permissions = "At least one permission must be selected.";
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      const selectedPermissions = allPermissions.filter(p => p.checked).map(p => p.id);
      
      if (isEditing) {
        // Update existing role
        setRoles(roles.map(role => 
          role.id === currentRoleId 
            ? { ...role, title: roleTitle, permissions: selectedPermissions } 
            : role
        ));
        setShowSuccess(true);
      } else {
        // Add new role
        const newRole = {
          id: roles.length + 1,
          title: roleTitle,
          permissions: selectedPermissions,
          status: "Active"
        };
        setRoles([...roles, newRole]);
        setShowSuccess(true);
      }
      
      // Close sidebar after submission
      setShowSidebar(false);
      
      // Reset form
      resetForm();

      // Hide success message after 3 seconds
      setTimeout(() => {
        setShowSuccess(false);
      }, 3000);
    }
  };

  const handlePermissionChange = (id) => {
    setAllPermissions(allPermissions.map(perm => 
      perm.id === id ? { ...perm, checked: !perm.checked } : perm
    ));
    // Clear permission error when user selects at least one
    if (errors.permissions && allPermissions.some(p => p.checked)) {
      setErrors({ ...errors, permissions: "" });
    }
  };

  const handleEdit = (roleId) => {
    const roleToEdit = roles.find(role => role.id === roleId);
    if (roleToEdit) {
      setRoleTitle(roleToEdit.title);
      setAllPermissions(allPermissions.map(perm => ({
        ...perm,
        checked: roleToEdit.permissions.includes(perm.id)
      })));
      setIsEditing(true);
      setCurrentRoleId(roleId);
      setShowSidebar(true);
    }
  };

  const resetForm = () => {
    setRoleTitle("");
    setAllPermissions(allPermissions.map(p => ({ ...p, checked: false })));
    setErrors({
      roleTitle: "",
      permissions: ""
    });
    setIsEditing(false);
    setCurrentRoleId(null);
  };

  // Check for mobile view
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close sidebar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const sidebar = document.querySelector('.sidebar-container');
      if (showSidebar && sidebar && !sidebar.contains(event.target)) {
        // Check if the click is not on the "Add Roles" button
        const addButton = document.querySelector('.add-role-button');
        if (addButton && !addButton.contains(event.target)) {
          setShowSidebar(false);
          resetForm();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSidebar]);

  return (
    <main className="main-content relative">
      {/* Success Message Popup */}
      {showSuccess && (
        <div className="fixed top-4 right-4 z-50 animate-fade-in">
          <div className="bg-[#635BFF] text-white px-4 py-2 rounded-lg shadow-lg flex items-center">
            <span className="material-symbols-outlined mr-2">check_circle</span>
            {isEditing ? "Role updated successfully!" : "Role created successfully!"}
          </div>
        </div>
      )}

      {/* Dark overlay when sidebar is open */}
      {showSidebar && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300"
          onClick={() => {
            setShowSidebar(false);
            resetForm();
          }}
        />
      )}

      {/* Main Content */}
      <div className={`transition-all duration-300 ${showSidebar ? '' : ''}`}>
        <div className="mb-6 flex flex-wrap items-center justify-between ml-4">
          <h2 className="text-xl font-semibold text-gray-800 ">
            Roles
          </h2>

          <nav>
            <ol className="flex items-center gap-1.5 mr-7">
              <li>
                <Link
                  className="inline-flex items-center gap-1.5 text-sm text-black "
                  to="/dashboard"
                >
                  Home /
                </Link>
              </li>
              <li className="text-sm text-gray-800 ">
                Roles
              </li>
            </ol>
          </nav>
        </div>

        <div className="mx-auto p-4 md:p-4">
          <div className="space-y-5 sm:space-y-6">
            <div className="rounded-2xl border border-gray-200 bg-white  ">
              <div className="px-5 py-4 sm:px-6 sm:py-5">
                <h3 className="text-base font-medium text-gray-800 dark:text-white/9">
                  <div className="flex justify-end">
                    <button 
                      className="add-role-button bg-[#635BFF] text-white px-4 py-2 rounded-lg shadow-lg hover:bg-[#4a28aa] transition"
                      onClick={() => {
                        setShowSidebar(true);
                        resetForm();
                      }}
                    >
                      Add Roles
                    </button>
                  </div>
                </h3>
              </div>
              <div className="p-5 border-t border-gray-100  sm:p-6">
                <div className="overflow-hidden rounded-xl border border-gray-200 bg-white  ">
                  <div className="max-w-full overflow-x-auto">
                    <table className="min-w-full">
                      <thead>
                        <tr
                          className="text-white border-b border-gray-100  whitespace-nowrap"
                          style={{ backgroundColor: "#635BFF" }}
                        >
                          <th className="px-5 py-3 sm:px-6">
                            <div className="flex items-center justify-center">
                              <p className="font-medium text-white text-sm ">
                                SI.No
                              </p>
                            </div>
                          </th>
                          <th className="px-5 py-3 sm:px-6">
                            <div className="flex items-center justify-center">
                              <p className="font-medium text-white text-sm ">
                                Title
                              </p>
                            </div>
                          </th>
                          <th className="px-5 py-3 sm:px-6">
                            <div className="flex items-center justify-center">
                              <p className="font-medium text-white text-sm ">
                                Permissions
                              </p>
                            </div>
                          </th>
                          
                          <th className="px-5 py-3 sm:px-6">
                            <div className="flex items-center justify-center">
                              <p className="font-medium text-white text-sm ">
                                Status
                              </p>
                            </div>
                          </th>
                          <th className="px-5 py-3 sm:px-6">
                            <div className="flex items-center justify-center">
                              <p className="font-medium text-white text-sm ">
                                Action
                              </p>
                            </div>
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {roles.map((role, index) => (
                          <tr key={role.id} className="whitespace-nowrap">
                            <td className="px-5 py-4 sm:px-6">
                              <div className="flex items-center justify-center">
                                <p className="text-black text-sm ">
                                  {index + 1}
                                </p>
                              </div>
                            </td>
                            <td className="px-5 py-4 sm:px-6">
                              <div className="flex items-center justify-center">
                                <p className="text-black text-sm ">
                                  {role.title}
                                </p>
                              </div>
                            </td>
                            <td className="px-5 py-4 sm:px-6">
                              <div className="flex items-center justify-center">
                                <p className="text-black text-sm ">
                                  {role.permissions.length} permissions
                                </p>
                              </div>
                            </td>
                            
                            <td className="px-5 py-4 sm:px-6">
                              <div className="flex items-center justify-center">
                                <button className={`rounded-full px-2 py-0.5 text-sm font-medium text-white ${
                                  role.status === "Active" ? "bg-[#047A48]" : "bg-[#DC2626]"
                                }`}>
                                  {role.status}
                                </button>
                              </div>
                            </td>
                            <td className="px-5 py-4 sm:px-6">
                              <div className="flex items-center justify-center">
                                <button 
                                  className="rounded-full bg-[#635BFF] px-3 py-0.5 text-sm font-medium text-white"
                                  onClick={() => handleEdit(role.id)}
                                >
                                  Edit
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Sidebar */}
      {showSidebar && (
        <div className={`sidebar-container fixed top-0 right-0 h-full bg-white shadow-lg z-50 transition-all duration-300 transform translate-x-0
          ${isMobile ? 'w-full max-w-sm' : 'w-96'}`}>
          <div className="h-full flex flex-col p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold text-gray-800">
                {isEditing ? "Edit Role" : "Add New Role"}
              </h3>
              <button 
                onClick={() => {
                  setShowSidebar(false);
                  resetForm();
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <hr></hr>
            
            <form onSubmit={handleSubmit} className="flex-1 flex flex-col mt-5">
              <div className="mb-4">
                <label className="block text-m font-medium text-gray-700 mb-2">
                  Role Title<span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className={`w-full p-2 border rounded-lg focus:outline-none focus:ring-2 placeholder:text-sm  ${
                    errors.roleTitle ? 'border-[#DC2626] focus:ring-[#DC2626]' : 'focus:ring-[#635BFF]'
                }`} 
                  value={roleTitle}
                  maxLength="100"
                  placeholder="Enter Your Role"
                  onChange={(e) => {
                    setRoleTitle(e.target.value);
                    if (errors.roleTitle) {
                      setErrors({ ...errors, roleTitle: "" });
                    }
                  }}
                />
                {errors.roleTitle && (
                  <p className="mt-1 text-sm text-red-500">{errors.roleTitle}</p>
                )}
              </div>

              <div className="mb-4">
                <label className="block text-m font-medium text-gray-700 mb-2">
                  Permissions<span className="text-red-500">*</span>
                </label>
                <div className={`w-full p-2 border rounded-lg focus:outline-none focus:ring-2 ${
                    errors.permissions ? 'border-[#DC2626] focus:ring-[#DC2626]' : 'focus:ring-[#635BFF]'
                }`}>
                  {allPermissions.map((permission) => (
                    <div key={permission.id} className="flex items-center">
                      <input 
                        type="checkbox" 
                        id={permission.id} 
                        className="mr-2"
                        checked={permission.checked}
                        onChange={() => handlePermissionChange(permission.id)}
                      />
                      <label htmlFor={permission.id}>{permission.name}</label>
                    </div>
                  ))}
                </div>
                {errors.permissions && (
                  <p className="mt-1 text-sm text-red-500">{errors.permissions}</p>
                )}
              </div>
              
              <div className="mt-auto">
                <div className="flex space-x-3">
                  <button
                    type="submit"
                    className="w-full bg-[#635BFF] text-white py-2 px-4 rounded-md hover:bg-[#4a28aa] transition"
                  >
                    {isEditing ? "Update" : "Submit"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowSidebar(false);
                      resetForm();
                    }}
                    className="w-full bg-gray-200 text-gray-800 py-2 px-4 rounded-md hover:bg-gray-300 transition"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </main>
  );
};

export default Roles;